import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { TextField } from '../ui/TextField';
import { TextArea } from '../ui/TextArea';
import { useAuth } from '../../context/AuthContext';
import { 
  FeatherX,
  FeatherSave,
  FeatherPlus,
  FeatherUser
} from '../ui/icons';
import './QuickCaptureForm.css';

const QuickCaptureForm = ({ onSuccess, onCancel, onAddMoreDetails, users }) => {
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    industry: '',
    phone: '',
    email: '',
    location: '',
    assigned_to: user?.id || '', // Auto-assign to current user
    willing_to_partner: false,
    initial_notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [nameValidation, setNameValidation] = useState({ isValid: true, message: '' });
  const [checkingName, setCheckingName] = useState(false);
  const [similarNames, setSimilarNames] = useState([]);

  // Auto-assign current user when user context loads
  useEffect(() => {
    if (user?.id && !formData.assigned_to) {
      setFormData(prev => ({
        ...prev,
        assigned_to: user.id
      }));
    }
  }, [user?.id, formData.assigned_to]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
    
    // Clear name validation when user starts typing
    if (field === 'name') {
      setNameValidation({ isValid: true, message: '' });
      setSimilarNames([]);
    }
  };

  // Debounced function to check for duplicate company names and find similar names
  const checkDuplicateName = React.useCallback(
    React.useMemo(() => {
      let timeoutId;
      return (name) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          if (!name || name.trim().length < 2) {
            setNameValidation({ isValid: true, message: '' });
            setSimilarNames([]);
            return;
          }
          
          setCheckingName(true);
          try {
            const api = require('../../services/api').default;
            
            // Try multiple search strategies to get better results
            const searchTerms = [];
            const inputWords = name.trim().split(/\s+/).filter(word => word.length >= 3);
            
            // Add the full search term
            searchTerms.push(name.trim());
            
            // Add individual words as search terms
            inputWords.forEach(word => {
              searchTerms.push(word);
            });
            
            console.log('Search terms to try:', searchTerms);
            
            // Get companies using the first word (most likely to match)
            const primarySearchTerm = inputWords.length > 0 ? inputWords[0] : name.trim();
            const response = await api.get(`/api/partner-companies?search=${encodeURIComponent(primarySearchTerm)}&limit=20`);
            
            // Debug: Log the API response
            console.log('API Response for search:', primarySearchTerm, response.data);
            console.log('Companies returned:', response.data.companies?.length || 0);
            console.log('All companies:', response.data.companies?.map(c => c.name) || []);
            
            const existingCompany = response.data.companies?.find(company => 
              company.name.toLowerCase() === name.trim().toLowerCase()
            );
            
            if (existingCompany) {
              setNameValidation({ 
                isValid: false, 
                message: `A company with the name "${existingCompany.name}" already exists.` 
              });
              setSimilarNames([]);
            } else {
              setNameValidation({ isValid: true, message: 'Company name is available.' });
              
              // Find similar names for suggestions
              console.log('Starting similarity check for:', name.trim());
              const similar = response.data.companies?.filter(company => {
                const inputName = name.trim().toLowerCase();
                const companyName = company.name.toLowerCase();
                
                // Split into words for better matching
                const inputWords = inputName.split(/\s+/).filter(word => word.length > 0);
                const companyWords = companyName.split(/\s+/).filter(word => word.length > 0);
                
                // Check for similar names using various methods
                const substringMatch = companyName.includes(inputName) || inputName.includes(companyName);
                const wordMatch = inputWords.some(inputWord => 
                  inputWord.length >= 3 && companyWords.some(companyWord => 
                    companyWord.includes(inputWord) || inputWord.includes(companyWord)
                  )
                );
                const companyWordMatch = companyWords.some(companyWord => 
                  companyWord.length >= 3 && inputWords.some(inputWord => 
                    inputWord.includes(companyWord) || companyWord.includes(inputWord)
                  )
                );
                const lengthMatch = (Math.abs(companyName.length - inputName.length) <= 3 &&
                 (companyName.includes(inputName.substring(0, Math.max(2, inputName.length - 1))) ||
                  inputName.includes(companyName.substring(0, Math.max(2, companyName.length - 1)))));
                const prefixMatch = inputWords.some(inputWord => 
                  inputWord.length >= 3 && companyName.startsWith(inputWord)
                ) || companyWords.some(companyWord => 
                  companyWord.length >= 3 && inputName.startsWith(companyWord)
                );
                
                const isSimilar = substringMatch || wordMatch || companyWordMatch || lengthMatch || prefixMatch;
                
                // Debug logging for all companies when searching for petes
                if (inputName.includes('petes') || companyName.includes('petes')) {
                  console.log('Similarity check for company:', companyName, {
                    inputName,
                    companyName,
                    inputWords,
                    companyWords,
                    isSimilar,
                    substringMatch,
                    wordMatch,
                    companyWordMatch,
                    lengthMatch,
                    prefixMatch
                  });
                }
                
                return isSimilar;
              }).slice(0, 3); // Limit to 3 suggestions
              
              console.log('Similar companies found:', similar?.map(c => c.name) || []);
              
              setSimilarNames(similar || []);
            }
          } catch (err) {
            console.error('Error checking company name:', err);
            // Don't show error for validation check failures
          } finally {
            setCheckingName(false);
          }
        }, 500); // 500ms debounce
      };
    }, []),
    []
  );

  // Check for duplicates when name changes
  React.useEffect(() => {
    if (formData.name) {
      checkDuplicateName(formData.name);
    }
  }, [formData.name, checkDuplicateName]);

  const handleKeyDown = (e) => {
    // Prevent form submission on Enter key unless it's the last field
    if (e.key === 'Enter' && e.target.name !== 'quick_company_notes') {
      e.preventDefault();
      // Focus the next field instead
      const form = e.target.form;
      const index = Array.from(form.elements).indexOf(e.target);
      const nextField = form.elements[index + 1];
      if (nextField && nextField.type !== 'submit') {
        nextField.focus();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear any previous errors
    setError('');
    
    // Basic validation
    if (!formData.name.trim()) {
      setError('Company name is required');
      return;
    }
    
    // Check for duplicate name validation
    if (!nameValidation.isValid) {
      setError(nameValidation.message);
      return;
    }
    
    // Additional validation
    if (formData.email && !formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    if (formData.website && !formData.website.startsWith('http')) {
      setError('Website should start with http:// or https://');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      console.log('Quick capture form data:', formData);
      
      // Clean up the data before submission
      const cleanedData = { ...formData };
      
      // Remove empty strings for optional fields
      if (!cleanedData.website) delete cleanedData.website;
      if (!cleanedData.industry) delete cleanedData.industry;
      if (!cleanedData.phone) delete cleanedData.phone;
      if (!cleanedData.email) delete cleanedData.email;
      if (!cleanedData.location) delete cleanedData.location;
      // Keep assigned_to as it's auto-assigned to current user
      if (!cleanedData.initial_notes) delete cleanedData.initial_notes;
      
      const submitData = {
        ...cleanedData,
        // Set default values for required API fields
        communication_style: 'formal',
        team_size: 1,
        current_capacity: 'free',
        response_time: 'immediate',
        // Convert arrays and objects to JSON strings
        contact_persons: JSON.stringify([]),
        available_skills: JSON.stringify([]),
        future_skills: JSON.stringify([]),
        tags: JSON.stringify([]),
        office_locations: JSON.stringify([]),
        language_proficiency: JSON.stringify([]),
        certifications: JSON.stringify([]),
        portfolio_links: JSON.stringify([]),
        primary_contact: JSON.stringify({ name: '', role: '', email: '', phone: '' }),
        decision_maker: JSON.stringify({ name: '', role: '', email: '' }),
        preferred_tools: JSON.stringify([]),
        technology_stack: JSON.stringify([]),
        skill_categories: JSON.stringify([]),
        experience_matrix: JSON.stringify({}),
        specializations: JSON.stringify([]),
        delivery_model: JSON.stringify([]),
        project_types: JSON.stringify([]),
        size_range: JSON.stringify([]),
        methodology: JSON.stringify([]),
        qa_processes: JSON.stringify([]),
        rate_ranges: JSON.stringify({}),
        contract_preferences: JSON.stringify([])
      };

      console.log('Submit data:', submitData);

      // Import the API service
      const api = require('../../services/api').default;
      const response = await api.post('/api/partner-companies', submitData);

      console.log('Response:', response.data);
      
      if (onSuccess) {
        setHasUnsavedChanges(false);
        onSuccess(response.data);
      }
    } catch (err) {
      console.error('Error submitting quick capture form:', err);
      
      // Handle different types of errors
      if (err.response?.data?.errors) {
        // Validation errors from API
        const errorMessages = err.response.data.errors.map(e => e.msg).join(', ');
        setError(`Validation errors: ${errorMessages}`);
      } else if (err.response?.data?.error) {
        // General API error
        if (err.response.status === 409) {
          // Duplicate company error
          setError(err.response.data.details || err.response.data.error);
        } else {
          setError(err.response.data.error);
        }
      } else if (err.message) {
        // Network or other errors
        setError(`Network error: ${err.message}`);
      } else {
        setError('Failed to save partner company. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="quick-capture-form">
      <div className="form-header">
        <h2>Quick Add Partner Company</h2>
        <Button
          variant="neutral-tertiary"
          icon={<FeatherX />}
          onClick={() => {
            if (hasUnsavedChanges && !loading) {
              if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                onCancel();
              }
            } else {
              onCancel();
            }
          }}
        />
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <Button
            variant="neutral-tertiary"
            size="sm"
            onClick={() => setError('')}
          >
            Dismiss
          </Button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-content">
          <div className="form-section">
            <h3>Essential Information</h3>
            <p className="section-description">
              Capture basic details to get started. You can add more information later.
            </p>
            
            <div className="form-grid">
              <div className="text-field">
                <label className="text-field-label">Company Name *</label>
                <div className="input-with-validation">
                  <input
                    name="quick_company_name"
                    id="quick_company_name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    onKeyDown={handleKeyDown}
                    required
                    placeholder="Enter company name"
                    autoComplete="off"
                    className={`text-field-input ${!nameValidation.isValid ? 'error' : nameValidation.isValid && nameValidation.message ? 'success' : ''}`}
                  />
                  {checkingName && (
                    <div className="validation-indicator checking">
                      <span>Checking...</span>
                    </div>
                  )}
                                        {!checkingName && nameValidation.message && (
                        <div className={`validation-indicator ${nameValidation.isValid ? 'success' : 'error'}`}>
                          <span>{nameValidation.message}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Similar names suggestions */}
                    {!checkingName && similarNames.length > 0 && nameValidation.isValid && (
                      <div className="similar-names-suggestions">
                        <div className="suggestions-header">
                          <span className="suggestions-icon">💡</span>
                          <span className="suggestions-text">Similar companies found:</span>
                        </div>
                        <div className="suggestions-list">
                          {similarNames.map((company, index) => (
                                                          <div key={company.id} className="suggestion-item">
                                <span className="suggestion-company-name">{company.name}</span>
                                <span className="form-status-badge">{company.partner_status?.current_status || 'No status'}</span>
                              </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
              
              <TextField label="Website">
                <TextField.Input
                  name="quick_company_website"
                  id="quick_company_website"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="https://example.com"
                  type="url"
                  autoComplete="off"
                />
              </TextField>
              
              <TextField label="Industry">
                <TextField.Input
                  name="quick_company_industry"
                  id="quick_company_industry"
                  value={formData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Technology, Healthcare, etc."
                  autoComplete="off"
                />
              </TextField>
              
              <TextField label="Contact Phone">
                <TextField.Input
                  name="quick_company_phone"
                  id="quick_company_phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="+1 (555) 123-4567"
                  autoComplete="off"
                />
              </TextField>
              
              <TextField label="Contact Email">
                <TextField.Input
                  name="quick_company_email"
                  id="quick_company_email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="contact@company.com"
                  type="email"
                  autoComplete="off"
                />
              </TextField>
              
              <TextField label="Location">
                <TextField.Input
                  name="quick_company_location"
                  id="quick_company_location"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="City, State/Country"
                  autoComplete="off"
                />
              </TextField>
              
              {/* Assigned To field hidden - auto-assigned to current user */}
            </div>
            
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  name="quick_willing_to_partner"
                  id="quick_willing_to_partner"
                  type="checkbox"
                  checked={formData.willing_to_partner}
                  onChange={(e) => handleInputChange('willing_to_partner', e.target.checked)}
                />
                Willing to Work
              </label>
              <p className="checkbox-description">
                Indicates whether this company is ready to start working (shows "Willing to Work" badge on cards)
              </p>
            </div>
            

            
            <TextArea label="Initial Notes">
              <TextArea.Input
                name="quick_initial_notes"
                id="quick_initial_notes"
                value={formData.initial_notes}
                onChange={(e) => handleInputChange('initial_notes', e.target.value)}
                placeholder="Quick observations, contact details, or next steps..."
                rows={3}
              />
            </TextArea>
          </div>
        </div>

        <div className="form-actions">
          <Button
            variant="neutral-tertiary"
            onClick={() => {
              if (hasUnsavedChanges && !loading) {
                if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
                  onCancel();
                }
              } else {
                onCancel();
              }
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="neutral-secondary"
            icon={<FeatherPlus />}
            onClick={onAddMoreDetails}
            disabled={loading}
          >
            Add More Details
          </Button>
          <Button
            type="submit"
            variant="brand-primary"
            icon={<FeatherSave />}
            loading={loading}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Company'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default QuickCaptureForm; 